/**
 * ActivityCommentsSection — comment form + list for activity participants.
 * Public: view comments (signed-out users see this below the registration form)
 * Auth-gated: post comments (must be registered for the activity)
 */
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, X, Image, AlertCircle } from 'lucide-react';
import { getActivityComments, postActivityComment, uploadUserImage } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { track } from '../utils/analytics';

const MAX_MEDIA = 5;
const isVideoUrl = (url) => /\.(mp4|mov|webm|avi|wmv)(\?.*)?$/i.test(url || '');

// ── Comment card ──────────────────────────────────────────────────────────────

function CommentCard({ comment, currentUserId }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const isPending = comment.status === 'under_moderation';
  const images = (comment.media_urls || []).filter(u => !isVideoUrl(u));
  const videos = (comment.media_urls || []).filter(u => isVideoUrl(u));

  const initials = (() => {
    const name = comment.user_name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase() || '?';
  })();

  return (
    <div className={`bg-gray-100 rounded-xl px-3 py-3 mb-2.5 last:mb-0 ${isPending ? 'opacity-70' : ''}`}>
      {/* Header: profile pic · name · date */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-shrink-0">
          {comment.user_avatar_url ? (
            <img src={comment.user_avatar_url} alt={comment.user_name} className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center text-[10px] font-bold text-green-700 leading-none">
              {initials}
            </div>
          )}
        </div>
        <p className="text-xs font-semibold text-gray-800 leading-none flex-1 min-w-0 truncate">
          {comment.user_name || 'Participant'}
        </p>
        <p className="text-[10px] text-gray-400 flex-shrink-0">
          {new Date(comment.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
        </p>
      </div>

      {isPending && (
        <p className="text-[10px] text-amber-600 flex items-center gap-1 mb-1">
          <AlertCircle size={9} /> Under review
        </p>
      )}

      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-snug">{comment.text}</p>

      {/* Images */}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {images.map((url, idx) => (
            <button
              key={idx}
              onClick={() => setLightboxIdx(idx)}
              className="rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid={`comment-image-${idx}`}
            >
              <img src={url} alt={`experience ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg" />
            </button>
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {videos.map((url, idx) => (
            <video key={idx} src={url} className="h-16 rounded-lg object-cover max-w-[80px]" controls playsInline />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
        >
          <img src={images[lightboxIdx]} alt="full size" className="max-h-screen max-w-screen object-contain" />
          <button className="absolute top-4 right-4 text-white" onClick={() => setLightboxIdx(null)}>
            <X size={24} />
          </button>
          {images.length > 1 && (
            <>
              {lightboxIdx > 0 && (
                <button className="absolute left-4 text-white text-2xl"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}>‹</button>
              )}
              {lightboxIdx < images.length - 1 && (
                <button className="absolute right-16 text-white text-2xl"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}>›</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Comment form ──────────────────────────────────────────────────────────────

function CommentForm({ activityId }) {
  const { user, openAuthModal } = useAuth();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const { mutate: submitComment, isPending: submitting } = useMutation({
    mutationFn: (data) => postActivityComment(activityId, data),
    onSuccess: (res) => {
      setText('');
      setMediaUrls([]);
      queryClient.setQueryData(['activity-comments', activityId], (old) => {
        const existing = old || [];
        return [...existing, res.data];
      });
      track('activity_comment_posted', { activity_id: activityId });
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || 'Failed to post comment. Please try again.';
      alert(msg);
    },
  });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_MEDIA - mediaUrls.length;
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const uploaded = await Promise.all(toUpload.map((f) => uploadUserImage(f)));
      const urls = uploaded.map((r) => r.data?.url || r.data?.file_url || '').filter(Boolean);
      setMediaUrls((prev) => [...prev, ...urls].slice(0, MAX_MEDIA));
    } catch { /* ignore */ } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeMedia = (idx) => setMediaUrls((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) { openAuthModal('comment'); return; }
    if (!text.trim() && mediaUrls.length === 0) return;
    submitComment({ text: text.trim(), media_urls: mediaUrls });
  };

  return (
    <form onSubmit={handleSubmit} className="mb-4">
      <div className="rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Share what it was like to participate..."
          rows={3}
          maxLength={1000}
          className="w-full px-3 pt-3 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none"
          data-testid="comment-textarea"
        />

        {/* Media previews */}
        {mediaUrls.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pb-2">
            {mediaUrls.map((url, idx) => (
              <div key={idx} className="relative">
                {isVideoUrl(url) ? (
                  <video src={url} className="h-16 w-16 rounded-lg object-cover" />
                ) : (
                  <img src={url} alt="preview" className="h-16 w-16 rounded-lg object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => removeMedia(idx)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center"
                >
                  <X size={8} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between px-3 pb-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || mediaUrls.length >= MAX_MEDIA}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-30"
              title="Add photo or video"
              data-testid="comment-add-media-btn"
            >
              <Image size={16} />
            </button>
            {uploading && <span className="text-xs text-gray-400">Uploading…</span>}
            {mediaUrls.length > 0 && (
              <span className="text-xs text-gray-400">{mediaUrls.length}/{MAX_MEDIA}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || uploading || (!text.trim() && mediaUrls.length === 0)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="comment-submit-btn"
          >
            <Send size={12} />
            {submitting ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/mp4,video/quicktime,video/webm"
        multiple
        onChange={handleFileChange}
        className="hidden"
        data-testid="comment-file-input"
      />
    </form>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

const ActivityCommentsSection = ({ activity, showForm = false }) => {
  const { user } = useAuth();
  const activityId = activity?.id;

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['activity-comments', activityId],
    queryFn: async () => (await getActivityComments(activityId)).data,
    enabled: !!activityId,
    staleTime: 30_000,
  });

  if (!activityId) return null;

  // Sort newest first
  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  // Hide entire section when there's nothing to show
  if (!isLoading && sortedComments.length === 0 && !showForm) return null;

  return (
    <div className="px-4 pt-4 pb-4" data-testid="activity-comments-section">
      {showForm && <CommentForm activityId={activityId} />}

      {isLoading && (
        <div className="space-y-3 animate-pulse">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-200 rounded w-24" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && sortedComments.length === 0 && showForm && (
        <p className="text-xs text-gray-400 text-center py-3">Be the first to share your experience!</p>
      )}

      {!isLoading && sortedComments.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3" data-testid="participant-experience-heading">
            Participant Experience
          </p>
          {sortedComments.map((c) => (
            <CommentCard key={c.id} comment={c} currentUserId={user?.id} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityCommentsSection;
